import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, Message, Panel, Placeholder } from 'rsuite';
import DatePicker from '../shared/DatePicker';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useAuth } from '../../contexts/AuthContext';
import { useCreatePayRate, useDeletePayRate, usePayRateHistory, useUpdatePayRate } from '../../hooks/useQueries';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// Intervals a money axis can be labelled in without looking arbitrary.
const NICE_AXIS_STEPS = [0.5, 1, 2, 5, 10, 20, 25, 50, 100];

const getRootCssVar = (name, fallback) => {
  try {
    if (typeof window === 'undefined' || !window.document?.documentElement) return fallback;
    const v = getComputedStyle(window.document.documentElement).getPropertyValue(name);
    const trimmed = String(v || '').trim();
    return trimmed || fallback;
  } catch (_e) {
    return fallback;
  }
};

const formatCurrency = (value) => {
  if (value == null || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const toDateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

const formatCurrencyInput = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const startOfDay = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const parseDateOnlyLocal = (value) => {
  if (!value) return null;
  const s = String(value);
  const dateOnly = s.length >= 10 ? s.slice(0, 10) : s;
  const parts = dateOnly.split('-').map((p) => Number(p));
  if (parts.length !== 3) return null;
  const [year, month, day] = parts;
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const formatMonthYearLabel = (d) => {
  if (!d) return '';
  const month = d.toLocaleString('en-US', { month: 'long' });
  const yy = String(d.getFullYear()).slice(-2);
  return `${month} '${yy}`;
};

// `showAddButton` lets a host that already has a header of its own (the profile
// page's Compensation card) put the add action there instead, driving it through
// the imperative handle below.
function PayRateHistory({ employeeId, hireDate, embedded = false, showAddButton = true }, ref) {
  const auth = useAuth();
  const canManagePayRates = Boolean(employeeId)
    && (
      auth.isAdmin()
      || auth.hasPermission('manage_all_employees')
      || auth.hasPermission('manage_coached_employees')
    );

  const themePrimary = useMemo(() => getRootCssVar('--bdc-primary', '#007374'), []);
  const themePrimaryRgb = useMemo(() => getRootCssVar('--bdc-primary-rgb', '0, 115, 116'), []);
  const themeFill = useMemo(() => `rgba(${themePrimaryRgb}, 0.15)`, [themePrimaryRgb]);

  const resolvedEmployeeId = employeeId || 'me';
  const historyQuery = usePayRateHistory(resolvedEmployeeId, { enabled: true });
  const createPayRate = useCreatePayRate();
  const updatePayRate = useUpdatePayRate();
  const deletePayRate = useDeletePayRate();

  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formValue, setFormValue] = useState({ pay_rate: null, effective_date: null, notes: '' });

  const minEffectiveDate = useMemo(() => startOfDay(hireDate), [hireDate]);

  const rows = useMemo(() => historyQuery.data || [], [historyQuery.data]);

  const chartPoints = useMemo(() => {
    const parsed = (rows || [])
      .map((r) => {
        const d = parseDateOnlyLocal(r?.effective_date);
        return {
          id: r?.id || null,
          effective_date: r?.effective_date || null,
          effectiveTime: d && !Number.isNaN(d.getTime()) ? d.getTime() : null,
          effectiveLabel: d ? formatMonthYearLabel(d) : String(r?.effective_date || ''),
          pay_rate: r?.pay_rate,
          notes: r?.notes || '',
        };
      })
      .filter((p) => p.effective_date && Number.isFinite(Number(p.pay_rate)));

    parsed.sort((a, b) => {
      const at = a.effectiveTime ?? 0;
      const bt = b.effectiveTime ?? 0;
      return at - bt;
    });

    return parsed;
  }, [rows]);

  const yAxisRange = useMemo(() => {
    const values = chartPoints.map((p) => Number(p.pay_rate)).filter((v) => Number.isFinite(v));
    if (values.length === 0) return null;

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const span = maxVal - minVal;

    // Headroom proportional to the spread rather than a flat $10, which left a
    // two-point line hugging the middle third of the plot. A single rate has no
    // spread to scale from, so it falls back to a share of the value itself.
    const pad = span > 0 ? Math.max(span * 0.12, 0.5) : Math.max(maxVal * 0.05, 1);
    const lo = Math.max(0, minVal - pad);
    const hi = maxVal + pad;

    // Aim for ~4 gaps, then round that interval up to a currency-friendly one
    // and snap the bounds to multiples of it. That gives an evenly stepped axis
    // of round numbers ($50, $55, $60 …) instead of whatever divides the range.
    const target = (hi - lo) / 4;
    const step = NICE_AXIS_STEPS.find((s) => s >= target) || Math.ceil(target / 100) * 100;

    const paddedMin = Math.max(0, Math.floor(lo / step) * step);
    const paddedMax = Math.ceil(hi / step) * step;

    if (paddedMax <= paddedMin) {
      return { min: paddedMin, max: paddedMin + step * 2, step };
    }

    return { min: paddedMin, max: paddedMax, step };
  }, [chartPoints]);

  const chartData = useMemo(() => {
    const labels = chartPoints.map((p) => p.effectiveLabel || String(p.effective_date));
    const data = chartPoints.map((p) => Number(p.pay_rate));
    return {
      labels,
      datasets: [
        {
          label: 'Pay Rate',
          data,
          borderColor: themePrimary,
          backgroundColor: themeFill,
          pointBackgroundColor: themePrimary,
          pointRadius: 3,
          tension: 0.25,
        }
      ]
    };
  }, [chartPoints, themeFill, themePrimary]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_event, elements) => {
        if (!canManagePayRates || !employeeId) return;
        if (!Array.isArray(elements) || elements.length === 0) return;
        const index = elements[0]?.index;
        if (!Number.isInteger(index)) return;
        const point = chartPoints[index];
        if (!point?.id) return;
        setEditingEntry(point);
        setFormValue({
          pay_rate: Number(point.pay_rate),
          effective_date: parseDateOnlyLocal(point.effective_date) || new Date(),
          notes: point.notes || '',
        });
        setModalOpen(true);
      },
      onHover: (event, elements) => {
        const target = event?.native?.target;
        if (!target) return;
        target.style.cursor = canManagePayRates && elements?.length ? 'pointer' : 'default';
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed?.y;
              return `Pay Rate: ${formatCurrency(value)}`;
            },
            afterLabel: (context) => {
              const idx = context.dataIndex;
              const p = chartPoints[idx];
              const lines = [];
              if (p?.notes) lines.push(`Notes: ${p.notes}`);
              return lines;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 0,
            autoSkip: true,
          },
          grid: {
            display: false,
          }
        },
        y: {
          type: 'linear',
          ...(yAxisRange ? { min: yAxisRange.min, max: yAxisRange.max } : { beginAtZero: true }),
          ticks: {
            // Fixed interval, so every gridline is the same number of dollars.
            ...(yAxisRange ? { stepSize: yAxisRange.step } : { maxTicksLimit: 5 }),
            callback: (v) => formatCurrency(v),
          }
        }
      }
    }),
    [canManagePayRates, chartPoints, employeeId, yAxisRange]
  );

  const openModal = () => {
    setEditingEntry(null);
    setFormValue({ pay_rate: null, effective_date: new Date(), notes: '' });
    setModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    canManage: canManagePayRates,
    openAdd: openModal,
  }));

  const closeModal = () => {
    setConfirmDeleteOpen(false);
    setModalOpen(false);
  };

  const submit = async () => {
    if (!canManagePayRates || !employeeId) return;

    const payload = {
      employeeId,
      pay_rate: formValue.pay_rate,
      effective_date: formValue.effective_date ? toDateOnly(formValue.effective_date) : null,
      notes: formValue.notes || null,
    };

    if (editingEntry?.id) {
      await updatePayRate.mutateAsync({
        ...payload,
        payRateId: editingEntry.id,
      });
    } else {
      await createPayRate.mutateAsync(payload);
    }

    setModalOpen(false);
  };

  const openDeleteConfirm = () => {
    if (!editingEntry?.id) return;
    setConfirmDeleteOpen(true);
  };

  const closeDeleteConfirm = () => {
    setConfirmDeleteOpen(false);
  };

  const remove = async () => {
    if (!canManagePayRates || !employeeId || !editingEntry?.id) return;

    await deletePayRate.mutateAsync({ employeeId, payRateId: editingEntry.id });
    setConfirmDeleteOpen(false);
    setModalOpen(false);
  };

  if (historyQuery.isLoading) {
    return <Placeholder.Paragraph rows={4} active />;
  }

  if (historyQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(historyQuery.error?.message || historyQuery.error)}
      </Message>
    );
  }

  const header = (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
      <div className="fw-semibold">Pay Rate History</div>
      {showAddButton && canManagePayRates && employeeId ? (
        <Button appearance="primary" size="sm" onClick={openModal}>
          <i className="bi bi-plus-lg me-2" />
          Add Pay Rate
        </Button>
      ) : null}
    </div>
  );

  const body = (
    <>
      {rows.length === 0 ? (
        <div className="text-muted">No pay rate history available.</div>
      ) : chartPoints.length < 1 ? (
        <div className="text-muted">No plottable pay rate points available.</div>
      ) : (
        <>
          {canManagePayRates && employeeId ? (
            <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Click a data point to edit that pay rate.
            </div>
          ) : null}
          <div style={{ height: embedded ? 180 : 260 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        onExited={() => setEditingEntry(null)}
        size="sm"
        backdrop="static"
        keyboard={!confirmDeleteOpen}
      >
        <Modal.Header>
          <Modal.Title>{editingEntry ? 'Edit Pay Rate' : 'Add Pay Rate'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createPayRate.error || updatePayRate.error || deletePayRate.error ? (
            <Message type="error" header="Error" showIcon style={{ marginBottom: 12 }}>
              {String(createPayRate.error?.message || updatePayRate.error?.message || deletePayRate.error?.message || createPayRate.error || updatePayRate.error || deletePayRate.error)}
            </Message>
          ) : null}

          <Form fluid formValue={formValue} onChange={setFormValue}>
            <Form.Group controlId="pay_rate">
              <Form.ControlLabel>Pay Rate</Form.ControlLabel>
              <Form.Control
                name="pay_rate"
                accepter={InputNumber}
                min={0}
                step={0.25}
                prefix="$"
                formatter={formatCurrencyInput}
              />
            </Form.Group>

            <Form.Group controlId="effective_date">
              <Form.ControlLabel>Effective Date</Form.ControlLabel>
              <Form.Control
                name="effective_date"
                accepter={DatePicker}
                oneTap
                isoWeek={false}
                weekStart={0}
                format="MM/dd/yyyy"
                shouldDisableDate={(date) => {
                  if (!minEffectiveDate) return false;
                  const candidate = startOfDay(date);
                  if (!candidate) return false;
                  return candidate < minEffectiveDate;
                }}
              />
            </Form.Group>

            <Form.Group controlId="notes">
              <Form.ControlLabel>Notes</Form.ControlLabel>
              <Form.Control name="notes" accepter={Input} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer
          left={editingEntry?.id ? (
            <NsiButton
              variant="danger"
              icon="trash"
              onClick={openDeleteConfirm}
              disabled={createPayRate.isPending || updatePayRate.isPending || deletePayRate.isPending}
            >
              Delete
            </NsiButton>
          ) : null}
        >
          <NsiButton onClick={closeModal} disabled={createPayRate.isPending || updatePayRate.isPending || deletePayRate.isPending}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submit} loading={createPayRate.isPending || updatePayRate.isPending}>
            {editingEntry ? 'Save Changes' : 'Add'}
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmDeleteOpen}
        onClose={closeDeleteConfirm}
        size="xs"
        role="alertdialog"
        backdrop="static"
        keyboard={!deletePayRate.isPending}
      >
        <Modal.Header>
          <Modal.Title>Delete Pay Rate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete this pay rate entry? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeDeleteConfirm} disabled={deletePayRate.isPending}>
            Cancel
          </NsiButton>
          <NsiButton variant="danger-solid" onClick={remove} loading={deletePayRate.isPending}>
            Delete
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </>
  );

  if (embedded) {
    return (
      <div>
        {header}
        <div style={{ marginTop: 10 }}>{body}</div>
      </div>
    );
  }

  return (
    <Panel bordered header={header}>
      {body}
    </Panel>
  );
}

export default forwardRef(PayRateHistory);
